<template>
  <div class="animated fadeIn">
    <b-row v-show="inSetup">
      <b-col>
        <h1>Account Registration</h1>
        <p>Welcome to HIPAAMATE! In order to setup your account we need some additional information about your practice location.</p>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready"/>
        <b-form @submit.prevent="onSubmit">
          <b-card header="Practice Location Information" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner/>
              </slot>
            </Loading>
            <hm-alert/>
            <div>
              <p>
                <strong>What is the practice name?</strong>
              </p>
            </div>
            <b-form-group>
              <b-form-input
                name="practiceName"
                type="text"
                class="form-control"
                placeholder="Practice Name"
                autocomplete="organization"
                v-model="practice.name"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('practiceName') && submitted }"
              ></b-form-input>
            </b-form-group>
            <div>
              <p>
                <strong>What is the physical address?</strong>
              </p>
            </div>
            <b-form-group label="Street Address">
              <b-form-input
                name="practiceAddress1"
                type="text"
                class="form-control"
                placeholder="Street and number"
                autocomplete="address-line1"
                v-model="practice.address1"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('practiceAddress1') && submitted }"
              ></b-form-input>
            </b-form-group>
            <b-form-group>
              <b-form-input
                name="practiceAddress2"
                type="text"
                class="form-control"
                placeholder="Suite, unit, building, floor, etc. (optional)"
                autocomplete="address-line2"
                v-model="practice.address2"
                :disabled="busy"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="City">
              <b-form-input
                name="practiceCity"
                type="text"
                class="form-control"
                placeholder="City"
                autocomplete="address-level2"
                v-model="practice.city"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('practiceCity') && submitted }"
              ></b-form-input>
            </b-form-group>
            <b-row>
              <b-col lg="6">
                <b-form-group label="State">
                  <b-form-input
                    name="practiceState"
                    type="text"
                    class="form-control"
                    placeholder="State"
                    autocomplete="address-level1"
                    v-model="practice.state"
                    v-validate="'required'"
                    :disabled="busy"
                    :class="{ 'is-invalid': errors.has('practiceState') && submitted }"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col lg="6">
                <b-form-group label="Zip">
                  <b-form-input
                    name="practiceZip"
                    type="text"
                    class="form-control"
                    placeholder="Zipcode (5 digit)"
                    autocomplete="postal-code"
                    maxlength="5"
                    v-model="practice.zip"
                    v-mask="'#####'"
                    v-validate="'required|numeric|length:5'"
                    :disabled="busy"
                    :class="{ 'is-invalid': errors.has('practiceZip') && submitted }"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <div>
              <p>
                <strong>What is the main phone number?</strong>
              </p>
            </div>
            <b-form-group>
              <the-mask
                name="practicePhone"
                :mask="'(###) ###-####'"
                type="text"
                class="form-control"
                placeholder="(xxx) xxx-xxxx"
                autocomplete="phone"
                v-model="practice.phone"
                v-validate="'required|numeric|length:10'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('practicePhone') && submitted }"
              ></the-mask>
            </b-form-group>
            <div slot="footer">
              <b-row>
                <b-col class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">
                    <span v-if="inSetup">
                      <i class="fa fa-refresh fa-spin" v-if="busy"></i> Next
                    </span>
                    <span v-else>Save</span>
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { practiceService } from '@/services';

export default {
  name: 'Practice.Location',
  data() {
    return {
      practice: {
        name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: null,
        phone: ''
      },
      ready: false,
      busy: false,
      submitted: false
    }
  },
  computed: {
    inSetup() {
      return this.$parent.inSetup;
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertSuccess: 'alert/success',
      alertError: 'alert/error',
      alertException: 'alert/exception'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return practiceService.setPractice(this.practice)
              .then(resp => {
                this.alertSuccess('Practice location changes saved successfully!');

                if (this.inSetup) {
                  this.$router.push('/setup/payment');
                }
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving practice location.' });
              });
          } else {
            this.alertError('Please complete practice form fields and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    }
  },
  created() {
    this.ready = false;

    practiceService.getPractice()
      .then(data => {
        this.practice = data;
      })
      .catch(err => {
        if (err.status !== 404) { // 404, practice may not be setup yet
          this.alertException({ err, message: 'Error loading practice location.' });
        }
      })
      .finally(() => {
        this.ready = true;
      });
  }
}
</script>